// app/providers.tsx
"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { Suspense, useEffect } from "react";

import ErrorBoundary from "@/components/ErrorBoundary"; // Import the new component
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { useSession } from "./session";

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
      api_host: "/ingest",
      ui_host: "https://us.posthog.com",
      capture_pageview: false, // Disable automatic pageview capture, as we capture manually
      capture_pageleave: true // Enable pageleave capture
    });
  }, []);

  return (
    <PHProvider client={posthog}>
      {/* Wrap children with ErrorBoundary inside the PostHog Provider */}
      <ErrorBoundary>
        <SuspendedPostHogPageView />
        {children}
      </ErrorBoundary>
    </PHProvider>
  );
}

function PostHogPageView() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const posthog = usePostHog();
  const { user } = useSession();
  // Track pageviews
  useEffect(() => {
    if (pathname && posthog) {
      let url = window.origin + pathname;

      posthog.identify(user?.Uid, {
        email: user?.Email,
        firstName: user?.FirstName,
        lastName: user?.LastName,
        company: user?.Account?.Name,
        plan: user?.Account?.CurrentSubscription?.Plan?.Name,
        planUid: user?.Account?.CurrentSubscription?.Plan?.Uid
      });

      if (searchParams.toString()) {
        url = url + "?" + searchParams.toString();
      }

      posthog.capture("$pageview", { $current_url: url });
    }
  }, [pathname, searchParams, posthog, user]);

  return null;
}

function SuspendedPostHogPageView() {
  return (
    <Suspense fallback={null}>
      <PostHogPageView />
    </Suspense>
  );
}
